import { component } from 'tsdi';
import { makeAutoObservable } from 'mobx';
import { CountryCodeEnum } from '../spa-frontend-i18n/dtos';
import {
    ConnectApiRateBundleDto,
    PublicApiDocumentIdentificationDto
} from './api/dtos';
import { getNumsRange, withLeadingZero } from './string-util';
import { injectTSDI } from './tsdi';
import { I18nStore } from './i18n-store';
import { featureEnabled } from './features';

export interface Option<V = string> {
    label: string;
    value: V;
}

export function optionsOfEnum<ENUM>(
    values: ENUM[],
    labelTranslateFn: (provider: ENUM) => string
): Option<ENUM>[] {
    return values.map((value) => ({
        value,
        label: labelTranslateFn(value)
    }));
}

export type GenderEnum = 'MALE' | 'FEMALE' | 'UNISEX';

export type YesNoEnum = false | true;

export type PaymentChoice = NonNullable<
    ConnectApiRateBundleDto['allowedPaymentChoices']
>[number];

export type DocumentTypeEnum = PublicApiDocumentIdentificationDto['documentType'];

const months = [
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12'
] as const;

type MonthNumber = typeof months[number];

export const isMonth = (val: string): val is MonthNumber =>
    months.includes(val as MonthNumber);

// ----

@component
export class OptionsStore {
    constructor() {
        makeAutoObservable(this);
    }

    public get i18nStore() {
        return injectTSDI(I18nStore);
    }

    public countryText = (countryCode: CountryCodeEnum | string) => {
        const { countries } = this.i18nStore;
        return (
            countries.find((pair) => pair.key === countryCode)?.value ??
            countryCode
        );
    };

    public get countryOptions() {
        const { countryOptions } = this.i18nStore;
        return countryOptions;
    }

    public dayOptions = getNumsRange(1, 31)
        .map(withLeadingZero)
        .map((day) => ({ value: day, label: day }));

    public monthText = (month: MonthNumber) => {
        const { translate } = this.i18nStore;
        switch (month) {
            case '01':
                return translate('commons.month.january');
            case '02':
                return translate('commons.month.february');
            case '03':
                return translate('commons.month.march');
            case '04':
                return translate('commons.month.april');
            case '05':
                return translate('commons.month.may');
            case '06':
                return translate('commons.month.june');
            case '07':
                return translate('commons.month.july');
            case '08':
                return translate('commons.month.august');
            case '09':
                return translate('commons.month.september');
            case '10':
                return translate('commons.month.october');
            case '11':
                return translate('commons.month.november');
            case '12':
                return translate('commons.month.december');
        }
    };

    public get monthOptions() {
        return optionsOfEnum([...months], this.monthText);
    }

    public yearOptions = getNumsRange(1920, 2010)
        .reverse()
        .map((val) => val.toString())
        .map((year) => ({ value: year, label: year }));

    public yesNoText = (value: YesNoEnum) => {
        const { translate } = this.i18nStore;
        switch (value) {
            case true:
                return translate('commons.yes');
            case false:
                return translate('commons.no');
        }
    };

    public get yesNoOptions() {
        return optionsOfEnum([false, true], this.yesNoText);
    }

    public genderText = (gender: GenderEnum) => {
        const { translate } = this.i18nStore;
        switch (gender) {
            case 'FEMALE':
                return translate('onlinepages.gender.female');
            case 'MALE':
                return translate('onlinepages.gender.male');
            case 'UNISEX':
                return translate('customer.gender.unisex');
        }
    };

    public get genderOptions() {
        return optionsOfEnum(['FEMALE', 'MALE', 'UNISEX'], this.genderText);
    }

    public paymentChoiceText = (choice: PaymentChoice) => {
        const { translate } = this.i18nStore;
        switch (choice) {
            case 'DIRECT_DEBIT':
                return translate('onlinepages.paymentchoice.directdebit');
            case 'CASH':
                return translate('onlinepages.paymentchoice.cash');
            case 'BANK_TRANSFER':
            case 'TWINT':
                throw new Error('not implemented');
            case 'CREDIT_CARD':
                return translate('onlinepages.paymentchoice.creditcard');
        }
    };

    public get paymentChoiceOptions() {
        return optionsOfEnum(
            [
                'DIRECT_DEBIT',
                ...(featureEnabled('CREDIT_CARD')
                    ? (['CREDIT_CARD'] as const)
                    : []),
                'CASH'
            ],
            this.paymentChoiceText
        );
    }

    public documentTypeText = (value: DocumentTypeEnum) => {
        const { translate } = this.i18nStore;

        switch (value) {
            case undefined:
                return translate('commons.none');
            case 'ID_CARD':
                return translate('enums.documenttype.idcard');
            case 'PASSPORT':
                return translate('enums.documenttype.passport');
            case 'DRIVERS_LICENCE':
                return translate('enums.documenttype.driverslicence');
            case 'RESIDENCE_PERMIT':
                return translate('enums.documenttype.residencepermit');
            case 'NATIONAL_ID_NUMBER':
                return translate('enums.documenttype.nationalidnumber');
            case 'OTHERS':
                return translate('enums.documenttype.others');
        }
    };

    public get documentTypeOptions() {
        return optionsOfEnum(
            ['ID_CARD', 'PASSPORT', 'DRIVERS_LICENCE', 'RESIDENCE_PERMIT'],
            this.documentTypeText
        );
    }
}
